import GameObject from '../../../GameObject/GameObject';
import AnimationClip from '../AnimationClip';
import { default as TNTSprite } from '../../../Prototype/Level/Card/TNT';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import TransformComponent from '../../../Component/Transform';
import { AwaitIterators, StartAnimation } from '../AnimationHelper';
import { Easing, Tween } from '@tweenjs/tween.js';
import { delay } from '../../../Utility/Task/TaskQueue';
import Renderable from '../../../Component/Renderable';
import SpriteComponent from '../../../Component/Render/Sprite';
import TNTExplosionEffect from '../../../Prototype/Effect/TNTExplosionEffect';
import Scene from '../../../Scene/Scene';
import { StartAudio } from '../../Audio/AudioHelper';
import { AUDIO_BOOSTER_TNT } from '../../Audio';
import { AudioTrackType } from '../../Audio/AudioClip';
import CardSystem from '../../Card/CardSystem';
import { DragonbonesAnimationEvent } from '../../../Component/Dragonbones';
import { showCardScoreEffectAt } from '../CardAnimationHelper';
import Point from '../../../Utility/Point';

export class BoosterTNT extends AnimationClip {
    public *execute(
        animator: GameObject,
        meta: {
            system: CardSystem;
            targets: { card: BaseCard; score: number }[];
        }
    ): IterableIterator<number> {
        meta.system.scene.emit('ToggleLevelHUD', false);

        const cards: { card: BaseCard; tnt: TNTSprite; score: number }[] = [];

        for (const target of meta.targets) {
            animator.addChild(target.card);

            // Create TNT sprites.
            const tnt = GameObject.Instantiate(TNTSprite);
            tnt.getComponent(SpriteComponent).visible = false;
            animator.addChild(tnt);

            cards.push({ card: target.card, tnt: tnt, score: target.score });

            // Ensure that the card is visible.
            target.card.getComponent(Renderable).visible = true;
        }

        // Fall animations.
        let fallOffsetIndex = 0;
        const fallAnimations = cards.map(cardTNT => {
            return this.tweenInTNT(cardTNT, fallOffsetIndex++);
        });

        yield* AwaitIterators(fallAnimations);

        // Pinch animations.
        const pinchAnimations = cards.map(cardTNT => {
            const tntTransform = cardTNT.tnt.getComponent(TransformComponent);
            const scaleTween = this.getTween(tntTransform.scale);
            return this.pinch(scaleTween);
        });

        yield* AwaitIterators(pinchAnimations);

        const explodeAnimations = cards.map((cardTNT, index) => {
            return this.explode(
                animator,
                cardTNT,
                index * 150,
                meta.system.scene
            );
        });

        meta.system.scene.emit('UpdateScoreProgress');

        yield* AwaitIterators(explodeAnimations);

        meta.system.scene.emit('ToggleLevelHUD', true);
    }

    private *tweenInTNT(
        cardTNT: any,
        fallOffsetIndex: number
    ): IterableIterator<number> {
        const cardTransform = cardTNT.card.getComponent(TransformComponent);
        const tntTransform = cardTNT.tnt.getComponent(TransformComponent);
        const tntSprite = cardTNT.tnt.getComponent(SpriteComponent);

        // Initial alpha.
        tntSprite.alpha = 0;

        const alphaTween = this.getTween(tntSprite);
        alphaTween.to({ alpha: 1 }, 100).delay(fallOffsetIndex * 100);

        // Set initial TNT position.
        tntTransform.position.set(
            cardTransform.position.x,
            cardTransform.position.y - 400
        );

        cardTNT.tnt.getComponent(SpriteComponent).visible = true;

        const positionTween = this.getTween(tntTransform);
        positionTween
            .to(
                {
                    x: tntTransform.position.x,
                    y: cardTransform.position.y,
                },
                500
            )
            .easing(Easing.Back.Out)
            .delay(fallOffsetIndex * 100);

        return yield* AwaitIterators([
            this.startTween(positionTween),
            this.startTween(alphaTween),
        ]);
    }

    private *explode(
        animator: GameObject,
        cardTNT: { card: BaseCard; tnt: TNTSprite; score: number },
        initialDelay: number,
        scene: Scene
    ): IterableIterator<number> {
        yield* delay(initialDelay);

        const tntTransform = cardTNT.tnt.getComponent(TransformComponent);

        // Wiggle.
        const rotationTween = this.getTween(tntTransform);
        rotationTween.easing(Easing.Quadratic.InOut);
        yield* this.wiggle(rotationTween);

        // Hide card.
        cardTNT.card.getComponent(Renderable).visible = false;

        const explosion = GameObject.Instantiate(TNTExplosionEffect);
        cardTNT.tnt.addChild(explosion);
        animator.addChild(explosion);

        StartAudio(scene, AUDIO_BOOSTER_TNT, AudioTrackType.SFX);

        explosion.on(DragonbonesAnimationEvent.COMPLETE, () =>
            explosion.destroy()
        );

        cardTNT.tnt.getComponent(SpriteComponent).visible = false;

        showCardScoreEffectAt(
            scene,
            animator,
            cardTNT.score,
            tntTransform.position.clone()
        );

        this.backgroundShake();
    }

    private backgroundShake() {
        const background = GameObject.findByName('backgroundSprite')[0];
        if (background) {
            StartAnimation(background, 'LevelScreenShake', {
                amplitude: 10,
                frequency: 25,
                damping: 0.9,
                position: new Point(),
            });
        }
    }

    private *wiggle(rotationTween: Tween): IterableIterator<number> {
        yield* this.rotationTo(rotationTween, Math.PI * 0.1, 75);
        yield* this.rotationTo(rotationTween, -Math.PI * 0.1, 75);
        yield* this.rotationTo(rotationTween, Math.PI * 0.1, 75);
        yield* this.rotationTo(rotationTween, -Math.PI * 0.1, 75);
        yield* this.rotationTo(rotationTween, Math.PI * 0.1, 75);
        yield* this.rotationTo(rotationTween, -Math.PI * 0.1, 75);
        yield* this.rotationTo(rotationTween, 0, 75);
    }

    private *pinch(scaleTween: Tween): IterableIterator<number> {
        yield* this.scaleTo(scaleTween, 0.8, 0.8, 125);
        yield* this.scaleTo(scaleTween, 1.2, 1.2, 125);
        yield* this.scaleTo(scaleTween, 1, 1, 125);
    }

    private *scaleTo(
        tween: Tween,
        scaleX: number,
        scaleY: number,
        delay: number
    ): IterableIterator<number> {
        tween.to({ x: scaleX, y: scaleY }, delay);
        yield* this.startTween(tween);
    }

    private *rotationTo(
        tween: Tween,
        rotation: number,
        delay: number
    ): IterableIterator<number> {
        tween.to({ rotation: rotation }, delay);
        yield* this.startTween(tween);
    }
}
